import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { DimensionDatum } from './hooks/useFetchDimensionData';
import { useReportErrorToBugsnag } from '../../../../util/bugsnagReporting';

const messages = defineMessages({
  notAvailable: {
    id: 'NoDataGaurd.notAvailable',
    defaultMessage: 'Not available',
  },
});

export const useStyles = makeStyles((theme: Theme) => ({
  emptyText: {
    color: theme.palette.text.disabled,
  },
}));

export type NoDataGuardProps<T extends DimensionDatum> = {
  children: React.ReactNode;
  dimensionDatum: T extends DimensionDatum ? T : never;
  results: unknown[] | undefined;
};

export function NoDataGuard<T extends DimensionDatum>({
  children,
  dimensionDatum,
  results,
}: NoDataGuardProps<T>): React.ReactElement | null {
  const formatMessage = useIntl().formatMessage;
  const classes = useStyles();

  const reportErrorToBugsnag = useReportErrorToBugsnag();

  const hasError = dimensionDatum.responseStats.hasError;

  if (hasError || (results && results.length === 0)) {
    const metadata = {
      systems: dimensionDatum.dimension.codes.map(code => code.system),
      dimension: dimensionDatum.dimension.name,
      hasError: dimensionDatum.responseStats.hasError,
      responseStatsId: dimensionDatum.responseStats.id,
      results: results?.length ?? 0,
      _rawData: dimensionDatum,
      subjectStats: dimensionDatum.responseStats.subjectStats,
    };
    if (results && results.length === 0 && !hasError) {
      reportErrorToBugsnag(
        new Error(
          'Tried to render lab results chart with no results. This usually means we got data from MRS /results, but not /history',
        ),
        metadata,
      );
    } else {
      reportErrorToBugsnag(
        new Error('Tried to render lab results chart with no results.'),
        metadata,
      );
    }
    return (
      <Box align="center" gap={2} fullHeight fullWidth padding={2}>
        <Text size="subbody">{dimensionDatum.dimension.name}</Text>
        <Text size="subbody" className={classes.emptyText}>
          {formatMessage(messages.notAvailable)}
        </Text>
      </Box>
    );
  } else {
    return <>{children}</> || null;
  }
}
